import { defineStore } from "pinia";
import { computed, getCurrentInstance, ref } from "vue";
import { until } from "@vueuse/core";
import { client, supportApiClient } from "@/lib/api.ts";
import type { NetworkAlertDto } from "@/types/networkAlert";

export type Customer = {
  id: number;
  account_id: number;
  auth0_sub: string;
  first_name: string;
  last_name: string;
  email: string;
  contact_email: string;
  dob: string;
  phone: string;
  marketing: string;
  stripe_id: string;
  created_at: string;
  updated_at: string;
};

export type Invoice = {
  total: number;
  status: string;
  created: number;
  hosted_invoice_url?: string;
  invoice_pdf: string;
};

export type Charge = {
  id: string;
  amount: number;
  amount_captured: number;
  amount_refunded: number;
  created: number;
  status: string;
  receipt_url?: string;
};

const API_CUSTOMER = "/customer";
const API_INVOICES = "/payment/invoices";
const API_CHARGES = "/payment/customer/charges";

export const useUserStore = defineStore("user", () => {
  const $auth =
    getCurrentInstance()?.appContext.app.config.globalProperties.$auth;

  const isAuthenticated = computed(() => $auth?.isAuthenticated());
  const authLoading = computed(() => $auth?.isLoading());
  const user = computed(() => $auth?.getUser());

  // Cached state and pending promise for Network Alerts
  const networkAlerts = ref<NetworkAlertDto[]>([]);
  let networkAlertsRequest: Promise<NetworkAlertDto[]> | null = null;
  let networkAlertsLastFetched: number | null = null;

  const CACHE_TTL_MS = 60 * 1000;

  async function getIdToken(): Promise<string | undefined> {
    await until(authLoading).toBe(false);
    const response = (await $auth?.getTokenSilently({
      detailedResponse: true,
    })) as { id_token: string };

    return response?.id_token;
  }

  async function getJSMCount() {
    const idToken = await getIdToken();
    const response = await supportApiClient.post<
      { data?: { count?: number } },
      Record<string, never>
    >(
      "/v1/jsm",
      {},
      {
        headers: { "x-token": idToken },
      },
    );

    return response.data?.data?.count;
  }

  async function getNetworkAlert(force = false): Promise<NetworkAlertDto[]> {
    const now = Date.now();
    const isCacheValid =
      networkAlertsLastFetched && now - networkAlertsLastFetched < CACHE_TTL_MS;

    // 1. Return cached alerts if within the 60s TTL and not forcing a refresh
    if (networkAlerts.value.length && isCacheValid && !force) {
      return networkAlerts.value;
    }

    // 2. Reuse active in-flight request if components call simultaneously
    if (networkAlertsRequest && !force) {
      return networkAlertsRequest;
    }

    // 3. Assign and execute API request promise
    networkAlertsRequest = (async () => {
      const idToken = await getIdToken();
      const response = await supportApiClient.post(
        "/v1/network-alert",
        {},
        {
          headers: {
            "x-token": idToken,
          },
        },
      );

      const data = (response.data || []) as NetworkAlertDto[];
      networkAlerts.value = data;
      networkAlertsLastFetched = Date.now();
      return data;
    })().finally(() => {
      networkAlertsRequest = null;
    });

    return networkAlertsRequest;
  }

  async function getNetworkAlertList(
    params: { page?: number; limit?: number; cursor?: string | null } = {},
  ) {
    const response = await supportApiClient.post(
      "/v1/network-alert-list",
      {},
      {
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          cursor: params.cursor || null,
        },
        headers: {
          "x-token": await getIdToken(),
        },
      },
    );

    return response.data;
  }

  async function authConfig() {
    await until(authLoading).toBe(false);

    if (!isAuthenticated.value) {
      window.location.href = "/login";
      return {};
    }

    const accessToken = await $auth?.getTokenSilently();

    return {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
  }

  let customer: Customer | null = null;

  async function getProfile(local = false) {
    if (!customer && !local) {
      try {
        customer = await client
          .get(API_CUSTOMER, await authConfig())
          .then((res) => res.data as Customer);
      } catch {
        // error intentionally ignored
      }
    }

    return customer;
  }

  async function setProfile(value: Customer) {
    const config = await authConfig();
    const response = customer
      ? await client.patch(API_CUSTOMER, value, config)
      : await client.post(API_CUSTOMER, value, config);

    return (customer = (response.data || null) as Customer);
  }

  const invoices = ref<Invoice[]>([]);
  const invoicesLoading = ref(false);
  const charges = ref<Charge[]>([]);
  const chargesLoading = ref(false);

  async function fetchInvoices(force = false) {
    if ((invoices.value.length && !force) || invoicesLoading.value) {
      return;
    }

    invoicesLoading.value = true;

    try {
      const res = await client.get(API_INVOICES, await authConfig());

      if (res.status === 200) {
        const data = res.data as Invoice[];
        invoices.value = data.filter((invoice) => invoice.status !== "draft");
      }
    } catch {
      // error intentionally ignored
    } finally {
      invoicesLoading.value = false;
    }
  }

  async function fetchCharges(force = false) {
    if ((charges.value.length && !force) || chargesLoading.value) {
      return;
    }

    chargesLoading.value = true;

    try {
      const profile = await getProfile();

      if (!profile?.id) {
        return;
      }

      const res = await client.post(
        `${API_CHARGES}?customer_id=${profile.id}`,
        {},
        await authConfig(),
      );

      if (res.status === 200) {
        charges.value = res.data?.data as Charge[];
      }
    } catch {
      // error intentionally ignored
    } finally {
      chargesLoading.value = false;
    }
  }

  return {
    getProfile,
    setProfile,
    $auth,
    isAuthenticated,
    authLoading,
    user,
    authConfig,
    getJSMCount,
    networkAlerts,
    getNetworkAlert,
    getNetworkAlertList,
    invoices,
    invoicesLoading,
    fetchInvoices,
    charges,
    chargesLoading,
    fetchCharges,
  };
});
